import React, { useState, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import { AccountingCycleManager } from '../../components/admin/AccountingCycleManager';
import { TrendingUp, TrendingDown, Scale, PiggyBank, PlusCircle, Loader2, RefreshCw, FileDown, Wallet } from 'lucide-react';
import { AccountingItem } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';

// -- Componente de Tarjeta de Resumen --
const SummaryCard: React.FC<{ title: string; value: number; icon: React.ReactNode; colorClass: string }> = ({ title, value, icon, colorClass }) => (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                {icon}
            </div>
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900">{value.toFixed(2)} €</p>
            </div>
        </div>
    </div>
);

// -- Componente para la Tabla de Movimientos --
const MovementTable: React.FC<{ movements: AccountingItem[] }> = ({ movements }) => (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        <div className="p-6"><h3 className="text-xl font-bold text-gray-900">Movimientos del Ciclo Seleccionado</h3></div>
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuente/Cliente</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y">
                    {movements.length === 0 ? (
                        <tr><td colSpan={4} className="text-center p-8 text-gray-500">No hay movimientos en este ciclo.</td></tr>
                    ) : (
                        movements.map((item) => (
                            <tr key={item.id}>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatDate(item.date)}</td>
                                <td className="px-6 py-4 text-sm text-gray-800">{item.description}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{item.source}</td>
                                <td className={`px-6 py-4 text-right font-medium text-sm ${item.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                    {item.type === 'income' ? '+' : '-'}{item.amount.toFixed(2)} €
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

// --- EL COMPONENTE PRINCIPAL DE LA PÁGINA ---
const ContabilidadPage: React.FC = () => {
    const { 
        cycles, allMovements, loading, clients,
        addExpense, addTransaction, syncRoyaleInvoices,
        syncMercury,
    } = useData();
    
    const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isSyncingMercury, setIsSyncingMercury] = useState(false);
    const [newExpenseState, setNewExpenseState] = useState({ expenseDate: new Date().toISOString().split('T')[0], amount: '', vendor: 'RoyaleHosting', description: '' });
    const [newTransactionState, setNewTransactionState] = useState({ transactionDate: new Date().toISOString().split('T')[0], amount: '', clientId: '', description: '' });

    const historicalSummary = useMemo(() => {
        if (!allMovements || allMovements.length === 0) return { totalIncome: 0, totalExpenses: 0, netProfit: 0, openingBalance: 0, available: 0 };
        const totalIncome = allMovements.filter(m => m.type === 'income').reduce((sum, item) => sum + item.amount, 0);
        const totalExpenses = allMovements.filter(m => m.type === 'expense').reduce((sum, item) => sum + item.amount, 0);
        return { totalIncome, totalExpenses, netProfit: totalIncome - totalExpenses };
    }, [allMovements]);

    const currentCycleSummary = useMemo(() => {
        if (!cycles || cycles.length === 0 || !allMovements) {
            return { totalIncome: 0, totalExpenses: 0, netProfit: 0, openingBalance: 0, available: 0 };
        }

        const openCycle = cycles.find(c => c.status === 'OPEN');

        if (!openCycle) {
            return { totalIncome: 0, totalExpenses: 0, netProfit: 0, openingBalance: 0, available: 0 };
        }

        const movementsInOpenCycle = allMovements.filter(m => m.cycle_id === openCycle.id);
        
        const totalIncome = movementsInOpenCycle.filter(m => m.type === 'income').reduce((sum, item) => sum + item.amount, 0);
        const totalExpenses = movementsInOpenCycle.filter(m => m.type === 'expense').reduce((sum, item) => sum + item.amount, 0);
        
        const openingBalance = parseFloat(String((openCycle as any).opening_balance ?? 0)) || 0;
        return { totalIncome, totalExpenses, netProfit: totalIncome - totalExpenses, openingBalance, available: openingBalance + totalIncome - totalExpenses };
    }, [cycles, allMovements]);
    
    const movementsForSelectedCycle = useMemo(() => {
        if (selectedCycleId === null || !allMovements) return [];
        return allMovements.filter(item => item.cycle_id === selectedCycleId);
    }, [allMovements, selectedCycleId]);

    const handleSync = async () => {
    setIsSyncing(true);
    try {
        // Pasamos 'null' como primer argumento porque la llamada a la API no necesita parámetros,
        // y el segundo argumento son las opciones para el refresco.
        const result = await syncRoyaleInvoices(null, { setLoading: false }); // <-- La llamada nueva y "silenciosa"
        toast.success(`Sincronización completa. Nuevos: ${result.newInvoices || 0}. Ignorados: ${result.ignoredInvoices || 0}.`);
    } catch (error: any) {
        toast.error(`Error al sincronizar: ${error.message}`);
    } finally {
        setIsSyncing(false);
    }
};

    const handleSyncMercury = async () => {
        setIsSyncingMercury(true);
        const toastId = toast.loading('Sincronizando con Mercury...');
        try {
            const result = await syncMercury(30, { setLoading: false });
            const nuevos = (result.incomeNew || 0) + (result.expenseNew || 0);
            const stats = result.stats || {};
            toast.success(`Mercury sincronizado. Nuevos: ${nuevos} (ingresos: ${result.incomeNew || 0}, gastos: ${result.expenseNew || 0}). Revisados: ${stats.scanned ?? '-'}, duplicados: ${stats.skippedDuplicate ?? '-'}.`, { id: toastId, duration: 8000 });
        } catch (error: any) {
            toast.error(`Error al sincronizar Mercury: ${error.message}`, { id: toastId, duration: 8000 });
        } finally {
            setIsSyncingMercury(false);
        }
    };

    const handleExpenseSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await addExpense({ ...newExpenseState, amount: parseFloat(newExpenseState.amount) });
            setNewExpenseState({ expenseDate: new Date().toISOString().split('T')[0], amount: '', vendor: 'RoyaleHosting', description: '' });
            toast.success('Gasto añadido con éxito.');
        } catch (error: any) {
            toast.error(`Error al añadir gasto: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleTransactionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await addTransaction({ ...newTransactionState, amount: parseFloat(newTransactionState.amount) });
            setNewTransactionState({ transactionDate: new Date().toISOString().split('T')[0], amount: '', clientId: '', description: '' });
            toast.success('Ingreso añadido con éxito.');
        } catch (error: any) {
            toast.error(`Error al añadir ingreso: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-8">
            {loading ? (
                <div className="p-8 text-center flex justify-center items-center h-96">
                    <Loader2 className="animate-spin text-blue-600" size={48} />
                </div>
            ) : (
                <>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Resumen del Ciclo Actual</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <SummaryCard title="Ingresos (Ciclo Actual)" value={currentCycleSummary.totalIncome} icon={<TrendingUp className="w-6 h-6 text-green-600" />} colorClass="bg-green-100" />
                            <SummaryCard title="Gastos (Ciclo Actual)" value={currentCycleSummary.totalExpenses} icon={<TrendingDown className="w-6 h-6 text-red-600" />} colorClass="bg-red-100" />
                            <SummaryCard title="Beneficio (Ciclo Actual)" value={currentCycleSummary.netProfit} icon={<Scale className="w-6 h-6 text-blue-600" />} colorClass="bg-blue-100" />
                            <SummaryCard title="Saldo Inicial (remanente anterior)" value={currentCycleSummary.openingBalance} icon={<Wallet className="w-6 h-6 text-indigo-600" />} colorClass="bg-indigo-100" />
                            <SummaryCard title="Total Disponible (saldo + beneficio)" value={currentCycleSummary.available} icon={<PiggyBank className="w-6 h-6 text-emerald-600" />} colorClass="bg-emerald-100" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Añadir Ingreso Manual</h3>
                            <form onSubmit={handleTransactionSubmit} className="space-y-4">
                                <div><label className="block text-sm font-medium text-gray-700">Fecha</label><input type="date" value={newTransactionState.transactionDate} onChange={(e) => setNewTransactionState(p => ({...p, transactionDate: e.target.value}))} className="mt-1 w-full p-2 border rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">Importe (€)</label><input type="number" value={newTransactionState.amount} onChange={(e) => setNewTransactionState(p => ({...p, amount: e.target.value}))} placeholder="100.00" step="0.01" className="mt-1 w-full p-2 border rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">Cliente</label><select value={newTransactionState.clientId} onChange={(e) => setNewTransactionState(p => ({...p, clientId: e.target.value}))} className="mt-1 w-full p-2 border rounded-md bg-white" required><option value="" disabled>Selecciona un cliente</option>{clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}</select></div>
                                <div><label className="block text-sm font-medium text-gray-700">Descripción</label><textarea value={newTransactionState.description} onChange={(e) => setNewTransactionState(p => ({...p, description: e.target.value}))} rows={2} placeholder="Pago servicio X..." className="mt-1 w-full p-2 border rounded-md" /></div>
                                <button type="submit" disabled={isSubmitting} className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
                                    {isSubmitting ? <Loader2 className="animate-spin" /> : <PlusCircle className="w-5 h-5 mr-2" />} Añadir Ingreso
                                </button>
                            </form>
                        </div>
                         <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Añadir Gasto Manual</h3>
                            <form onSubmit={handleExpenseSubmit} className="space-y-4">
                                <div><label className="block text-sm font-medium text-gray-700">Fecha</label><input type="date" value={newExpenseState.expenseDate} onChange={(e) => setNewExpenseState(p => ({...p, expenseDate: e.target.value}))} className="mt-1 w-full p-2 border rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">Importe (€)</label><input type="number" value={newExpenseState.amount} onChange={(e) => setNewExpenseState(p => ({...p, amount: e.target.value}))} placeholder="50.00" step="0.01" className="mt-1 w-full p-2 border rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">Proveedor</label><input type="text" value={newExpenseState.vendor} onChange={(e) => setNewExpenseState(p => ({...p, vendor: e.target.value}))} placeholder="RoyaleHosting" className="mt-1 w-full p-2 border rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">Descripción</label><textarea value={newExpenseState.description} onChange={(e) => setNewExpenseState(p => ({...p, description: e.target.value}))} rows={2} placeholder="Factura servidor..." className="mt-1 w-full p-2 border rounded-md" /></div>
                                <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
                                    {isSubmitting ? <Loader2 className="animate-spin" /> : <PlusCircle className="w-5 h-5 mr-2" />} Añadir Gasto
                                </button>
                            </form>
                        </div>
                    </div>

                    <AccountingCycleManager 
                        selectedCycleId={selectedCycleId}
                        onSelectCycle={setSelectedCycleId} 
                    />

                    {selectedCycleId !== null && (
                        <MovementTable movements={movementsForSelectedCycle} />
                    )}
                    
                    <div className="mt-12 space-y-8">
                         <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Acciones y Resumen Histórico</h2>
                             <div className="flex items-center gap-4 mb-6">
                                <button onClick={handleSync} disabled={isSyncing} className="bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center transition-colors">{isSyncing ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="w-5 h-5 mr-2" />}Sincronizar Facturas</button>
                                <button onClick={handleSyncMercury} disabled={isSyncingMercury} className="bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center transition-colors">{isSyncingMercury ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="w-5 h-5 mr-2" />}Sincronizar Mercury</button>
                                <CSVLink data={allMovements} headers={[{ label: "Fecha", key: "date" },{ label: "Descripción", key: "description" },{ label: "Fuente", key: "source" },{ label: "Importe", key: "amount" },{ label: "Tipo", key: "type" }]} filename={"historial-completo.csv"} className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 flex items-center" target="_blank"><FileDown className="w-5 h-5 mr-2" />Exportar Historial</CSVLink>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                <SummaryCard title="Ingresos Históricos" value={historicalSummary.totalIncome} icon={<TrendingUp className="w-6 h-6 text-green-600" />} colorClass="bg-green-100" />
                                <SummaryCard title="Gastos Históricos" value={historicalSummary.totalExpenses} icon={<TrendingDown className="w-6 h-6 text-red-600" />} colorClass="bg-red-100" />
                                <SummaryCard title="Balance Total" value={historicalSummary.netProfit} icon={<PiggyBank className="w-6 h-6 text-yellow-600" />} colorClass="bg-yellow-100" />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ContabilidadPage;